<script setup lang="ts">
import {
  computed,
  ref,
} from 'vue';
import { useRouter } from 'vue-router';

import type { IForgotPasswordEmailSchema } from '@modules/forgot-password/types';
import type {
  FormFlattenKey,
  FormFlattenValue,
  FormSchema,
} from '@modules/forms/components/VForm/types';
import {
  FORGOT_PASSWORD_CONFIRM_TOKEN_ROUTE_NAME,
  TRANSLATION_GROUP_FORGOT_PASSWORD,
} from '@modules/forgot-password/constants';
import { useForgotPasswordEmailMutation } from '@modules/forgot-password/mutations';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { useI18n } from '@modules/i18n/composables';
import {
  assert,
  isOptionalString,
} from '@utils/guards';

const router = useRouter();
const { getTranslation } = useI18n();
const email = ref<string>();

const {
  mutateAsync,
  isLoading,
  errors,
  clearError,
} = useForgotPasswordEmailMutation();

const isDisabled = computed(() => !email.value || !!Object.keys(errors.value ?? {}).length);

const formSchema = computed<FormSchema<IForgotPasswordEmailSchema>>(() => ({
  email: {
    type: EFormInputTypes.TEXT,
    label: getTranslation(TRANSLATION_GROUP_FORGOT_PASSWORD, 'NEW_EMAIL').value,
    inputType: 'email',
    value: email.value,
    required: true,
  },
}));

function onInteracted<
  T extends IForgotPasswordEmailSchema,
  K extends FormFlattenKey<T>,
>(
  key: K,
  value: FormFlattenValue<T, K> | undefined,
): void {
  if (key === 'email') {
    assert(isOptionalString(value));
    email.value = value;
  }

  clearError(key);
}

async function onSubmit(values: IForgotPasswordEmailSchema): Promise<void> {
  await mutateAsync(values);
  void router.replace({
    name: FORGOT_PASSWORD_CONFIRM_TOKEN_ROUTE_NAME,
  });
}
</script>

<template>
  <div v-data-qa-auto-id="'ForgotPasswordEnterEmailPage'" class="input-max-width">
    <div class="text-center text-3xl font-light uppercase">
      {{ $getTranslation(TRANSLATION_GROUP_FORGOT_PASSWORD, 'NEW_HEADER') }}
    </div>
    <div class="my-6 text-center text-muted-color">
      {{ $getTranslation(TRANSLATION_GROUP_FORGOT_PASSWORD, 'NEW_TEXT') }}
    </div>
    <VForm
      :schema="formSchema"
      :disabled="isDisabled"
      :submit-btn-label="$getTranslation(TRANSLATION_GROUP_FORGOT_PASSWORD, 'NEW_SUBMIT')"
      is-buttons-centered
      :custom-errors="errors"
      :is-loading="isLoading"
      @interacted="onInteracted"
      @submit="onSubmit"
    />
  </div>
</template>
